if (!customElements.get("additional-actions-sample")) {
  customElements.define(
    "additional-actions-sample",
    class AdditionalActionsSample extends HTMLElement {
      connectedCallback() {
        this.actionsRoot = this.closest(".additional-actions");
        this.pdp = this.closest("section.main-product-pdp");
        this.sampleButton = this.querySelector('[data-action="get-sample"]');
        this.sampleCopy = this.querySelector("[data-sample-copy]");
        this.sampleVariantInput = this.querySelector('input[name="id"]');
        this.variantPicker = this.pdp?.querySelector("variant-selects, variant-radios");
        this.variants = this.parseJSONNode('[data-additional-actions-variants]');
        this.optionNames = this.parseJSONNode('[data-additional-actions-options]');
        this.moneyFormat = this.pdp?.querySelector("product-form")?.dataset?.moneyFormat || "${{amount}}";
        this.defaultSampleCopy = this.sampleCopy?.textContent?.trim() || "Get Sample";

        this.sizeOptionIndex = this.getSizeOptionIndex();
        this.sampleVariantMap = this.buildSampleVariantMap();


        if (!this.sampleButton || !this.sampleVariantInput || !this.variantPicker) return;

        this.onVariantPickerChange = this.handleVariantPickerChange.bind(this);
        this.variantPicker.addEventListener("change", this.onVariantPickerChange);

        this.syncSampleVariantId();
      }

      disconnectedCallback() {
        if (this.variantPicker && this.onVariantPickerChange) {
          this.variantPicker.removeEventListener("change", this.onVariantPickerChange);
        }
      }

      parseJSONNode(selector) {
        const node = this.actionsRoot?.querySelector(selector);
        if (!node) return [];
        try {
          return JSON.parse(node.textContent);
        } catch (error) {
          console.error("[additional-actions] failed parsing json", { selector, error });
          return [];
        }
      }

      normalizeValue(value) {
        return String(value || "").trim().toLowerCase();
      }

      buildKey(options) {
        if (!Array.isArray(options)) return "";
        return options
          .map((option, index) => ({ option, index }))
          .filter(({ index }) => index !== this.sizeOptionIndex)
          .map(({ option }) => this.normalizeValue(option))
          .join("||");
      }

      getSizeOptionIndex() {
        const namedSizeIndex = this.optionNames.findIndex((name) => this.normalizeValue(name).includes("size"));
        if (namedSizeIndex >= 0) return namedSizeIndex;

        const optionCount = this.optionNames.length || this.variants[0]?.options?.length || 0;
        for (let index = 0; index < optionCount; index += 1) {
          const hasSampleValueAtIndex = this.variants.some((variant) =>
            this.normalizeValue(variant?.options?.[index]).includes("sample")
          );
          if (hasSampleValueAtIndex) return index;
        }

        return -1;
      }

      buildSampleVariantMap() {
        if (this.sizeOptionIndex < 0) return {};

        return this.variants.reduce((map, variant) => {
          if (!variant || !Array.isArray(variant.options)) return map;
          const sizeValue = this.normalizeValue(variant.options[this.sizeOptionIndex]);
          if (!sizeValue.includes("sample")) return map;

          const key = this.buildKey(variant.options);
          if (!key) return map;

          const existing = map[key];
          if (!existing || (!existing.available && variant.available)) {
            map[key] = {
              id: variant.id,
              available: Boolean(variant.available),
              sizeLabel: String(variant.options[this.sizeOptionIndex] || "").trim(),
              price: Number(variant.price) || 0,
            };
          }

          return map;
        }, {});
      }

      getCurrentOptions() {
        const currentVariant = this.variantPicker?.currentVariant;
        if (currentVariant?.options?.length) return currentVariant.options;

        const selects = Array.from(this.variantPicker.querySelectorAll("select"));
        if (selects.length) return selects.map((select) => select.value);

        const fieldsets = Array.from(this.variantPicker.querySelectorAll("fieldset"));
        if (!fieldsets.length) return [];

        return fieldsets.map((fieldset) => fieldset.querySelector("input:checked")?.value).filter(Boolean);
      }

      handleVariantPickerChange() {
        this.syncSampleVariantId();
      }

      formatPrice(cents) {
        const numericCents = Number(cents) || 0;

        if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
          const formatted = window.Shopify.formatMoney(numericCents, this.moneyFormat);
          return String(formatted).replace(/<[^>]*>/g, "").trim();
        }

        return `$${(numericCents / 100).toFixed(2).replace(/\.00$/, "")}`;
      }

      getSampleCopy(sampleVariant) {
        if (!sampleVariant) return this.defaultSampleCopy;

        const sizeLabel = String(sampleVariant.sizeLabel || "").trim() || "Sample";
        const formattedPrice = this.formatPrice(sampleVariant.price);
        return `${sizeLabel} ${formattedPrice} - Add to Cart`;
      }

      getFallbackSampleVariant() {
        const sampleVariants = Object.values(this.sampleVariantMap || {});
        if (!sampleVariants.length) return null;

        return sampleVariants.find((variant) => variant?.available) || sampleVariants[0];
      }

      syncSampleVariantId() {
        const currentOptions = this.getCurrentOptions();
        const key = this.buildKey(currentOptions);
        const sampleVariant = this.sampleVariantMap[key] || this.getFallbackSampleVariant();
        const nextId = sampleVariant?.id ? String(sampleVariant.id) : "";

        this.sampleVariantInput.value = nextId;
        this.sampleButton.disabled = !nextId;
        if (this.sampleCopy) {
          this.sampleCopy.textContent = this.getSampleCopy(sampleVariant);
        }

      }
    }
  );
}

if (!customElements.get("additional-actions-footage-calculator")) {
  customElements.define(
    "additional-actions-footage-calculator",
    class AdditionalActionsFootageCalculator extends HTMLElement {
      connectedCallback() {
        this.pdp = this.closest("section.main-product-pdp");
        this.container = this.pdp?.querySelector(".js-product-footage");
        this.openTrigger = this.querySelector('[data-action="open-footage-calculator"], .trigger-footage-calculator');
        this.anchorOpenTriggers = Array.from(document.querySelectorAll('a[href="#footage-calculator"]'));

        if (!this.pdp || !this.container || !this.openTrigger) return;
        if (this.pdp.id) this.container.dataset.pdpId = this.pdp.id;

        this.background = this.container.querySelector(".js-product-footage-background");
        this.card = this.container.querySelector(".js-product-footage-card");
        this.primaryClose = this.container.querySelector(".product-footage__card-close");
        this.closeTriggers = Array.from(this.container.querySelectorAll(".js-product-footage-close"));

        if (!this.background || !this.card || !this.primaryClose) return;

        // Move modal to <body> to escape parent stacking contexts.
        if (this.container.parentElement !== document.body) {
          this.containerMountPoint = document.createComment("product-footage-mount");
          this.container.parentElement.insertBefore(this.containerMountPoint, this.container);
          document.body.appendChild(this.container);
        }

        this.timeline = new TimelineMax({ paused: true });
        this.timeline.fromTo(this.background, 0.3, { autoAlpha: 0 }, { autoAlpha: 0.5, ease: Sine.easeOut });

        if (window.innerWidth < 768) {
          this.timeline.fromTo(this.card, 0.9, { yPercent: 100 }, { yPercent: 0, ease: Expo.easeOut }, "-=0.1");
          this.timeline.fromTo(
            this.primaryClose,
            0.8,
            { autoAlpha: 0, scale: 0.5, rotate: 90 },
            { autoAlpha: 1, scale: 1, rotate: 0, ease: Expo.easeOut },
            "-=0.4"
          );
        } else {
          this.timeline.fromTo(this.card, 0.9, { xPercent: 100 }, { xPercent: 0, ease: Expo.easeOut }, "-=0.1");
          this.timeline.fromTo(
            this.primaryClose,
            0.8,
            { autoAlpha: 0, scale: 0.5, rotate: 90, xPercent: -50 },
            { autoAlpha: 1, scale: 1, rotate: 0, xPercent: -50, ease: Expo.easeOut },
            "-=0.4"
          );
        }

        this.onOpen = this.handleOpen.bind(this);
        this.onClose = this.handleClose.bind(this);
        this.onTriggerEnter = this._onTriggerEnter.bind(this);
        this.onTriggerLeave = this._hideMaterialTooltip.bind(this);
        if (this.openTrigger) {
          this.openTrigger.addEventListener("click", this.onOpen);
          this.openTrigger.addEventListener("mouseenter", this.onTriggerEnter);
          this.openTrigger.addEventListener("mouseleave", this.onTriggerLeave);
        }
        this.anchorOpenTriggers.forEach((trigger) => trigger.addEventListener("click", this.onOpen));
        this.closeTriggers.forEach((trigger) => trigger.addEventListener("click", this.onClose));

        this.ensureCalculatorInitialized();
      }

      disconnectedCallback() {
        if (this.openTrigger && this.onOpen) {
          this.openTrigger.removeEventListener("click", this.onOpen);
          this.openTrigger.removeEventListener("mouseenter", this.onTriggerEnter);
          this.openTrigger.removeEventListener("mouseleave", this.onTriggerLeave);
        }
        this._hideMaterialTooltip();
        if (this.anchorOpenTriggers?.length && this.onOpen) {
          this.anchorOpenTriggers.forEach((trigger) => trigger.removeEventListener("click", this.onOpen));
        }

        if (this.closeTriggers?.length && this.onClose) {
          this.closeTriggers.forEach((trigger) => trigger.removeEventListener("click", this.onClose));
        }

        this.unlockPageScroll();

        if (this.containerMountPoint?.parentNode && this.container?.parentElement === document.body) {
          this.containerMountPoint.parentNode.insertBefore(this.container, this.containerMountPoint);
          this.containerMountPoint.parentNode.removeChild(this.containerMountPoint);
          this.containerMountPoint = null;
        }
      }

      handleOpen(event) {
        event.preventDefault();

        // Block opening if the product has a Material option but none is selected.
        // The tooltip is shown via hover (_onTriggerEnter), not on click.
        const hasMaterialOption = !!this.pdp?.querySelector("input[type='radio'][name='Material']");
        const materialSelected = !!this.pdp?.querySelector("input[type='radio'][name='Material']:checked");
        if (hasMaterialOption && !materialSelected) return;

        this.ensureCalculatorInitialized();
        this.lockPageScroll();
        $(this.container).show();
        this.timeline.play(0);
      }

      _onTriggerEnter() {
        const hasMaterialOption = !!this.pdp?.querySelector("input[type='radio'][name='Material']");
        const materialSelected = !!this.pdp?.querySelector("input[type='radio'][name='Material']:checked");
        if (hasMaterialOption && !materialSelected) {
          this._showMaterialTooltip(this.openTrigger);
        }
      }

      _showMaterialTooltip(anchor) {
        this._hideMaterialTooltip();

        const tooltip = document.createElement("div");
        tooltip.className = "footage-calculator-tooltip";
        tooltip.textContent = "Please select a material first.";
        document.body.appendChild(tooltip);
        this._materialTooltip = tooltip;

        // Position below the anchor using fixed coords to avoid scroll offsets.
        const rect = anchor.getBoundingClientRect();
        tooltip.style.top = `${rect.bottom + 8}px`;
        tooltip.style.left = `${rect.left}px`;
      }

      _hideMaterialTooltip() {
        this._materialTooltip?.remove();
        this._materialTooltip = null;
      }

      handleClose(event) {
        event.preventDefault();

        setTimeout(() => {
          $(this.container).hide();
          this.unlockPageScroll();
        }, 800);

        gsap.to(this.background, 0.3, { autoAlpha: 0, ease: Sine.easeOut });

        if (window.innerWidth < 768) {
          gsap.to(this.card, 0.7, { yPercent: 100, ease: Expo.easeOut });
          gsap.to(this.primaryClose, 0.8, { autoAlpha: 0, scale: 0.5, rotate: 90, ease: Expo.easeOut });
        } else {
          gsap.to(this.card, 0.7, { xPercent: 100, ease: Expo.easeOut });
          gsap.to(this.primaryClose, 0.8, {
            autoAlpha: 0,
            scale: 0.5,
            rotate: 90,
            xPercent: -50,
            ease: Expo.easeOut,
          });
        }

        gsap.to(this.primaryClose, 0.7, { autoAlpha: 0, ease: Expo.easeOut }, "-=0.3");
      }

      ensureCalculatorInitialized() {
        if (this.container.dataset.calculatorReady === "true") return;
        if (typeof window.initCalculator !== "function") return;

        window.initCalculator();
        this.container.dataset.calculatorReady = "true";
      }

      lockPageScroll() {
        if (this.isScrollLocked) return;

        document.documentElement.classList.add("footage-calculator-open");
        document.body.classList.add("footage-calculator-open");
        if (typeof window.lockBodyScroll === "function") {
          window.lockBodyScroll("footage-calculator");
        }
        this.isScrollLocked = true;
      }

      unlockPageScroll() {
        if (!this.isScrollLocked) return;

        document.documentElement.classList.remove("footage-calculator-open");
        document.body.classList.remove("footage-calculator-open");
        if (typeof window.unlockBodyScroll === "function") {
          window.unlockBodyScroll("footage-calculator");
        }
        this.isScrollLocked = false;
      }
    }
  );
}